import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import './fonts.css';
import '../node_modules/web-ui-components/lib/app.vm.no-fonts.css';
import React, { StrictMode } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import Cookies from 'js-cookie';
import { WebUIThemeProvider } from 'web-ui-components/lib/utilities/themes';
import { createAppStore } from './createAppStore';
import { EnvironmentContext } from './context/EnvironmentContext';
import { App } from './App';
import { fetchConfig, updateNativeAppData } from './actionCreators/config';
import { queryParser } from './utils/queryParseUtil';
import { removeHashUrl } from './utils/genericUtil';
import DeviceContext from 'context/DeviceContext';
import { history } from './routes';
import { navigateToPreviousScreen } from 'actionCreators/navigation';
import { webAppLoaded } from 'utils/mobileUtils';
import { getBankIdFromSelector } from 'actionCreators/search';
import { Analytics } from 'utils/analytics';

if (window.history.scrollRestoration) {
  window.history.scrollRestoration = 'manual';
}

Analytics.inject();

const store = createAppStore();
const parsedParams = queryParser();
if (process.env.NODE_ENV === 'development' && parsedParams?.stub === 'true') {
  // the check for Cypress is on a seperate line so that webpack can cleanly
  // eliminate this in production as "dead" code.

  import('./mocks/browser.js').then(({ worker }) => {
    worker.start({ onUnhandledRequest: 'bypass' });
  });
}

Promise.all([store.dispatch(fetchConfig())]).then(() => {
  removeHashUrl();
  store.dispatch(Analytics.send());

  /**
   * @note
   * For branch assisted, "Shoulder-to-Shoulder" journeys, an authenticated device
   * within a branch visits an internal site to acquire a cookie with the branch sort code (storeCode)
   * This helps us identify in-branch journeys versus other applications.
   */
  const branchDetails = Cookies.getJSON('branchDetails');

  if (branchDetails) {
    const { storeCode, bankId } = branchDetails;

    if (!parsedParams.bankId) {
      // Read bankId from cookie if no bankId is provided
      parsedParams.bankId =
        !parsedParams.accessToken && ['CB', 'YB'].includes(bankId.toUpperCase())
          ? bankId.toUpperCase()
          : null;
    }
    parsedParams.branchSortCode = storeCode;
  }

  //get bank Id if bank id not provided and access token not available
  if (!parsedParams.bankId && !parsedParams.accessToken) {
    store.dispatch(getBankIdFromSelector());
  }

  /**
   * This function will call by Native App while Web App load first time and update client context and user context of Native App
   * nativeData structure  - {xbpi_client_context,xbpi_service_context,is_native }
   */
  window.webAppInit = function (nativeData) {
    nativeData &&
      store.dispatch(
        updateNativeAppData(typeof nativeData === 'string' ? JSON.parse(nativeData) : nativeData)
      );
  };
  /**
   * This function will call by Native App when user click on back button of Native App
   * _nativeData is require as per contract interface
   */
  window.backClick = function (_nativeData) {
    store.dispatch(navigateToPreviousScreen());
  };

  webAppLoaded();

  ReactDOM.render(
    <DeviceContext.Provider>
      <EnvironmentContext.Provider>
        <WebUIThemeProvider bankID={parsedParams.bankId || 'CB'} brandID={'VM'}>
          <Provider store={store}>
            <ConnectedRouter history={history} noInitialPop>
              <StrictMode>
                <App parsedParams={parsedParams} />
              </StrictMode>
            </ConnectedRouter>
          </Provider>
        </WebUIThemeProvider>
      </EnvironmentContext.Provider>
    </DeviceContext.Provider>,
    document.getElementById('root')
  );

  if (process.env.NODE_ENV !== 'production') {
    if (module.hot) {
      module.hot.accept('./App', () => {
        const HotReloadedApp = require('./App').default;
        ReactDOM.render(
          <DeviceContext.Provider>
            <EnvironmentContext.Provider>
              <WebUIThemeProvider bankID={parsedParams.bankId || 'CB'} brandID={'VM'}>
                <Provider store={store}>
                  <ConnectedRouter history={history} noInitialPop>
                    <StrictMode>
                      <HotReloadedApp parsedParams={parsedParams} />
                    </StrictMode>
                  </ConnectedRouter>
                </Provider>
              </WebUIThemeProvider>
            </EnvironmentContext.Provider>
          </DeviceContext.Provider>,
          document.getElementById('root')
        );
      });
    }
  }
});
